<template>
    <div ref="rootRef" class="effects-section">
        <!-- 移动端 Tab 切换 -->
        <div class="lg:hidden mb-5 mt-10">
            <div class="flex items-center gap-6">
                <button
                    class="relative pb-2 text-[15px] font-medium transition-colors"
                    :class="activeTab === 'video' ? 'text-white' : 'text-white/40'"
                    @click="activeTab = 'video'"
                >
                    {{ t('menus.videoEffects') }}
                    <span
                        v-if="activeTab === 'video'"
                        class="absolute left-0 right-0 bottom-0 h-[2px] bg-[#99F768] rounded-full"
                    ></span>
                </button>
                <button
                    class="relative pb-2 text-[15px] font-medium transition-colors"
                    :class="activeTab === 'photo' ? 'text-white' : 'text-white/40'"
                    @click="activeTab = 'photo'"
                >
                    {{ t('menus.photoEffects') }}
                    <span
                        v-if="activeTab === 'photo'"
                        class="absolute left-0 right-0 bottom-0 h-[2px] bg-[#99F768] rounded-full"
                    ></span>
                </button>
            </div>
        </div>

        <!-- AI 视频特效 -->
        <div
            class="effects-block"
            :class="{ hidden: activeTab !== 'video' && !isDesktop }"
        >
            <!-- PC端标题 -->
            <h2 class="hidden lg:block text-white text-base font-bold mb-5 mt-10">
                AI {{ t('menus.videoEffects') }}
            </h2>
            <div class="effects-container">
                <!-- 移动端 Grid 布局 -->
                <div class="lg:hidden grid grid-cols-2 gap-3 pb-20">
                    <div
                        v-for="item in videoTemplates"
                        :key="item.id"
                        class="aspect-[3/4] overflow-hidden rounded-xl"
                    >
                        <EffectsCard
                            :item="item"
                            auto-size
                            @click="handleVideoTemplate"
                            @use-template="handleVideoTemplate"
                        />
                    </div>
                </div>
                <!-- PC端瀑布流布局 -->
                <div
                    class="video-effects-grid hidden lg:block"
                    ref="videoGridRef"
                    :style="{ height: `${videoContainerHeight}px` }"
                >
                    <div
                        v-for="item in videoGridItems"
                        :key="item.id"
                        class="effect-item absolute transition-all duration-300"
                        :style="item.style"
                    >
                        <EffectsCard
                            :item="item"
                            :width="columnWidth"
                            :height="item.calculatedHeight"
                            @click="handleVideoTemplate"
                            @use-template="handleVideoTemplate"
                            @media-load="onVideoMediaLoad"
                        />
                    </div>
                </div>
                <!-- 底部渐变遮罩和查看所有按钮 -->
                <div class="view-all-overlay">
                    <NuxtLink
                        to="/video-effects"
                        class="view-all-btn inline-flex items-center gap-2 px-6 py-3"
                    >
                        <span>{{ t('effects.viewAllVideoEffects') }}</span>
                    </NuxtLink>
                </div>
            </div>
        </div>

        <!-- AI 图片特效 -->
        <div
            class="effects-block lg:mt-10"
            :class="{ hidden: activeTab !== 'photo' && !isDesktop }"
        >
            <!-- PC端标题 -->
            <h2 class="hidden lg:block text-white text-base font-bold mb-5 mt-10">
                AI {{ t('menus.photoEffects') }}
            </h2>
            <div class="effects-container">
                <!-- 移动端 Grid 布局 -->
                <div class="lg:hidden grid grid-cols-2 gap-3 pb-20">
                    <div
                        v-for="item in photoTemplates"
                        :key="item.id"
                        class="aspect-square overflow-hidden rounded-xl"
                    >
                        <PhotoEffectsCard
                            :item="item"
                            auto-size
                            @click="handlePhotoTemplate"
                            @use-template="handlePhotoTemplate"
                        />
                    </div>
                </div>
                <!-- PC端 Flex 布局 -->
                <div class="effects-grid hidden lg:flex">
                    <div
                        v-for="item in photoTemplates"
                        :key="item.id"
                        class="effect-item"
                        :style="{ width: `${columnWidth}px` }"
                    >
                        <PhotoEffectsCard
                            :item="item"
                            :width="columnWidth"
                            @click="handlePhotoTemplate"
                            @use-template="handlePhotoTemplate"
                        />
                    </div>
                </div>
                <!-- 底部渐变遮罩和查看所有按钮 -->
                <div class="view-all-overlay">
                    <NuxtLink
                        to="/photo-effects"
                        class="view-all-btn inline-flex items-center gap-2 px-6 py-3"
                    >
                        <span>{{ t('effects.viewAllPhotoEffects') }}</span>
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { navigateTo } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { getEffectTemplatePageApi } from '~/api/effects'
import EffectsCard from '~/components/Effects/EffectsCard.vue'
import PhotoEffectsCard from '~/components/Effects/PhotoEffectsCard.vue'

const { t, locale } = useI18n()

// 移动端 Tab 切换状态
const activeTab = ref<'video' | 'photo'>('video')

// 判断是否为桌面端（用于控制内容显示）
const isDesktop = ref(true)

// 检测屏幕宽度
const checkDesktop = () => {
    if (typeof window !== 'undefined') {
        isDesktop.value = window.innerWidth >= 1024 // lg breakpoint
    }
}

// 进入视口后再请求（避免把慢接口放进首屏关键链）
const rootRef = ref<HTMLElement | null>(null)
const hasLoaded = ref(false)

// 视频模板数据
const videoTemplates = ref<any[]>([])

// 图片模板数据
const photoTemplates = ref<any[]>([])

// 网格布局相关
const videoGridRef = ref<HTMLElement | null>(null)
const columnWidth = ref(240)
const columnCount = ref(5)
const gap = 16

// 默认宽高比（3:4 视觉更平衡）
const defaultAspectRatio = 3 / 4

// 存储视频卡片实际宽高比（通过媒体加载事件获取）
const videoAspectRatios = ref<Map<string, number>>(new Map())

// 预加载图片并获取尺寸（带 2 秒超时）
const preloadImage = (
    url: string,
    timeout = 2000
): Promise<{ width: number; height: number }> => {
    return new Promise(resolve => {
        if (!url) {
            resolve({ width: 3, height: 4 }) // 默认 3:4
            return
        }

        const img = new Image()
        let resolved = false

        // 超时处理：超过指定时间使用默认值
        const timer = setTimeout(() => {
            if (!resolved) {
                resolved = true
                resolve({ width: 3, height: 4 }) // 超时使用默认 3:4
            }
        }, timeout)

        img.onload = () => {
            if (!resolved) {
                resolved = true
                clearTimeout(timer)
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
            }
        }

        img.onerror = () => {
            if (!resolved) {
                resolved = true
                clearTimeout(timer)
                resolve({ width: 3, height: 4 }) // 失败使用默认 3:4
            }
        }

        img.src = url
    })
}

// 获取实际宽高比（优先使用加载后的实际尺寸）
const getVideoAspectRatio = (item: any): number => {
    const actualRatio = videoAspectRatios.value.get(item.id)
    if (actualRatio) return actualRatio
    return defaultAspectRatio
}

// 根据宽高比计算卡片高度
const calculateVideoItemHeight = (item: any): number => {
    const aspectRatio = getVideoAspectRatio(item)
    return Math.ceil(columnWidth.value / aspectRatio)
}

// 媒体加载完成（预加载方案下不再需要重新布局，仅作为兜底）
const onVideoMediaLoad = (
    item: any,
    actualWidth: number,
    actualHeight: number
) => {
    // 如果预加载失败但媒体加载成功，可以更新宽高比
    if (
        actualWidth > 0 &&
        actualHeight > 0 &&
        !videoAspectRatios.value.has(item.id)
    ) {
        videoAspectRatios.value.set(item.id, actualWidth / actualHeight)
    }
}

// 瀑布流布局计算
const videoGridItems = computed(() => {
    if (!videoGridRef.value || videoTemplates.value.length === 0) return []

    const heights = new Array(columnCount.value).fill(0)

    return videoTemplates.value.map(item => {
        const colIndex = heights.indexOf(Math.min(...heights))
        const height = calculateVideoItemHeight(item)

        const left = colIndex * (columnWidth.value + gap)
        const top = heights[colIndex]

        heights[colIndex] += height + gap

        return {
            ...item,
            calculatedHeight: height,
            style: {
                left: `${left}px`,
                top: `${top}px`,
                width: `${columnWidth.value}px`,
            },
        }
    })
})

// 视频容器高度
const videoContainerHeight = computed(() => {
    if (videoTemplates.value.length === 0) return 400

    const heights = new Array(columnCount.value).fill(0)
    videoTemplates.value.forEach(item => {
        const colIndex = heights.indexOf(Math.min(...heights))
        heights[colIndex] += calculateVideoItemHeight(item) + gap
    })

    // +80 为底部遮罩留出空间
    return Math.max(...heights, 400) + 80
})

// 计算列宽和列数
const calculateColumnWidth = () => {
    if (!videoGridRef.value) return

    const containerWidth = videoGridRef.value.clientWidth
    const maxColumnWidth = 300
    const minColumnWidth = 180
    const baseColumnWidth = 240

    // 根据容器宽度动态计算列数
    let columns = Math.floor((containerWidth + gap) / (baseColumnWidth + gap))
    columns = Math.max(2, columns) // 最少 2 列

    // 计算列宽并限制在合理范围内
    let width = Math.floor((containerWidth - gap * (columns - 1)) / columns)
    width = Math.max(minColumnWidth, Math.min(maxColumnWidth, width))

    columnCount.value = columns
    columnWidth.value = width
}

// 加载视频模板（不指定 tagId，获取所有）
const loadVideoTemplates = async () => {
    try {
        const res = await getEffectTemplatePageApi({
            type: 'VIDEO',
            pageNum: 1,
            pageSize: 12,
            lang: locale.value,
        })

        if (res.status === 200 && res.data?.content) {
            const templates = res.data.content.map((item: any) => ({
                id: String(item.id),
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
                cover: item.cover_url,
                video: item.video_urls?.[0] || '',
                title: item.name,
                likes: 0,
            }))

            // 预加载所有封面图获取宽高比（带 2 秒超时）
            await Promise.all(
                templates.map(async (item: any) => {
                    if (item.cover) {
                        const { width, height } = await preloadImage(item.cover)
                        if (width > 0 && height > 0) {
                            videoAspectRatios.value.set(item.id, width / height)
                        }
                    }
                })
            )

            videoTemplates.value = templates
        }
    } catch (error) {
        console.error('加载视频模板失败:', error)
    }
}

// 加载图片模板（不指定 tagId，获取所有）
const loadPhotoTemplates = async () => {
    try {
        const res = await getEffectTemplatePageApi({
            type: 'IMAGE',
            pageNum: 1,
            pageSize: 20,
            lang: locale.value,
        })

        if (res.status === 200 && res.data?.content) {
            photoTemplates.value = res.data.content.map((item: any) => ({
                id: String(item.id),
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
                cover: item.cover_url,
                title: item.name,
                likes: 0,
            }))
        }
    } catch (error) {
        console.error('加载图片模板失败:', error)
    }
}

// 点击视频模板
const handleVideoTemplate = (item: any) => {
    const slug =
        item.slug || item.title?.toLowerCase().replace(/\s+/g, '-') || item.id
    navigateTo(`/video-effects/${slug}`)
}

// 点击图片模板
const handlePhotoTemplate = (item: any) => {
    const slug =
        item.slug || item.title?.toLowerCase().replace(/\s+/g, '-') || item.id
    navigateTo(`/photo-effects/${slug}`)
}

onMounted(() => {
    nextTick(() => {
        calculateColumnWidth()
    })
    window.addEventListener('resize', calculateColumnWidth)

    // 检测屏幕宽度（用于移动端 Tab 切换）
    checkDesktop()
    window.addEventListener('resize', checkDesktop)

    // 当组件即将进入视口时再触发请求（提前 200px 预热）
    const { stop } = useIntersectionObserver(
        rootRef,
        ([entry]) => {
            if (entry?.isIntersecting && !hasLoaded.value) {
                hasLoaded.value = true
                loadVideoTemplates()
                loadPhotoTemplates()
                stop()
            }
        },
        { rootMargin: '200px 0px' }
    )
})

onUnmounted(() => {
    window.removeEventListener('resize', calculateColumnWidth)
    window.removeEventListener('resize', checkDesktop)
})
</script>

<style lang="less" scoped>
.effects-section {
    width: 100%;
}

.effects-block {
    margin-bottom: 40px;
}

.effects-container {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    z-index: 1;
}

// 视频特效瀑布流布局
.video-effects-grid {
    position: relative;
    min-height: 400px;
}

// 图片特效 flex 布局（display 由 Tailwind 的 lg:flex 控制）
.effects-grid {
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: 80px; // 为底部遮罩留出空间
}

.effect-item {
    flex-shrink: 0;
}

.view-all-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 140px;
    background: linear-gradient(
        to top,
        rgba(13, 14, 18, 1) 0%,
        rgba(13, 14, 18, 0.95) 30%,
        rgba(13, 14, 18, 0.7) 60%,
        transparent 100%
    );
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 24px;
    z-index: 10;
}

.view-all-btn {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 100px;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;

    &:hover {
        border-color: rgba(255, 255, 255, 0.2);
        color: #fff;
        font-weight: 500;
        background: rgba(255, 255, 255, 0.1);
    }
}

</style>
